import { X } from 'lucide-react';
import { type ReactNode } from 'react';

import { type ChatAppearance } from '@apps/web/components/phoebe_chat/chat_appearance';
import { cn } from '@apps/web/lib/utils';

export function ReferenceChipTray({
    appearance,
    testId,
    children,
}: {
    readonly appearance: ChatAppearance;
    readonly testId: string;
    readonly children: ReactNode;
}) {
    return (
        <div
            data-testid={testId}
            className={
                appearance === 'v3-agent'
                    ? 'mb-2 flex flex-wrap gap-2'
                    : 'border-border bg-background mb-2 rounded-xl border p-1'
            }
        >
            {children}
        </div>
    );
}

export function ReferenceRemoveButton({
    appearance,
    label,
    testId,
    onClick,
}: {
    readonly appearance: ChatAppearance;
    readonly label: string;
    readonly testId: string;
    readonly onClick: () => void;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            data-testid={testId}
            className={cn(
                'flex shrink-0 items-center justify-center rounded',
                appearance === 'v3-agent'
                    ? 'size-3.5 text-[#617578] hover:text-[#002429]'
                    : 'text-muted-foreground hover:text-foreground h-5 w-5',
            )}
            onClick={onClick}
        >
            <X
                className={
                    appearance === 'v3-agent' ? 'size-[11px]' : 'h-3.5 w-3.5'
                }
            />
        </button>
    );
}

export function ReferenceChip({
    appearance,
    leading,
    hideLeadingInV3 = false,
    children,
    removeLabel,
    removeTestId,
    rowTestId,
    onRemove,
}: {
    readonly appearance: ChatAppearance;
    readonly leading?: ReactNode;
    readonly hideLeadingInV3?: boolean;
    readonly children: ReactNode;
    readonly removeLabel: string;
    readonly removeTestId: string;
    readonly rowTestId: string;
    readonly onRemove: () => void;
}) {
    return (
        <div
            data-testid={rowTestId}
            className={
                appearance === 'v3-agent'
                    ? 'flex h-7 items-center gap-1.5 rounded-full border border-[#DBE3E3] bg-[#F7FAFA] px-2.5 text-[#244045]'
                    : 'hover:bg-muted/60 flex items-center gap-2 rounded-lg px-1.5 py-1'
            }
        >
            {appearance === 'v3-agent' && hideLeadingInV3 ? null : leading}
            {children}
            <ReferenceRemoveButton
                appearance={appearance}
                label={removeLabel}
                testId={removeTestId}
                onClick={onRemove}
            />
        </div>
    );
}
